/**
 * The muster as a sheet a crew can sign.
 *
 * This exists to close a specific gap. When an employee files their own
 * correction, their submission is the evidence they agreed with it. Employees
 * who never touch the portal file nothing, so every day of their register is the
 * employer's assertion alone — which is exactly what a wage dispute turns on.
 * A periodic sheet the crew signs is the substitute for that missing consent.
 *
 * So the export is not a data dump. It carries the things a signature has to be
 * *against*: which days were asserted rather than captured, who is attesting,
 * and a column to sign in.
 */

import { printSymbol, tallyRow } from "./musterTally";

const CSV_SAFE = /^[=+\-@\t\r]/;

// A leading =, +, - or @ makes a spreadsheet treat the cell as a formula. Names
// and remarks are user-supplied, so they get neutralised on the way out.
const cell = (value) => {
  const s = value == null ? "" : String(value);
  const guarded = CSV_SAFE.test(s) ? `'${s}` : s;
  return /[",\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
};

const row = (cells) => cells.map(cell).join(",");

/**
 * @param {object}   opts
 * @param {Array}    opts.rows      resolved muster rows ({ name, aliasId, days, summary })
 * @param {number}   opts.year
 * @param {number}   opts.month     0-indexed
 * @param {string}   opts.monthName
 * @param {number}   opts.totalDays
 * @param {string}   [opts.generatedBy]
 */
export const buildMusterCsv = ({ rows: musterRows, year, monthName, totalDays, generatedBy, scopeLabel }) => {
  const lines = [];

  // Name the scope on the sheet. A departmental sheet that reads as if it were
  // the whole roster is worse than no sheet: someone signs it believing the
  // absent names simply were not there that month.
  lines.push(row([`Attendance muster — ${monthName} ${year}${scopeLabel ? ` · ${scopeLabel}` : ""}`]));
  lines.push(row([`Generated ${new Date().toLocaleString()}${generatedBy ? ` by ${generatedBy}` : ""}`]));
  // the legend belongs on the sheet itself: whoever signs it will not have the
  // application open beside them
  // "L Late" used to sit here beside the leave codes, which put one letter
  // against two meanings on a document people sign. A late arrival is a day the
  // employee was present; the minutes belong in the register.
  lines.push(row(["P Present", "A Absent", "MP Missing punch", "L / CL / SL Leave", "WO Weekly off", "H Holiday"]));
  lines.push(row(["* marks a day whose punch was corrected rather than captured by the device"]));
  lines.push(
    row([
      "Present reads against Working days — the days rostered, excluding weekly offs, holidays and days still to come.",
    ])
  );
  // Spelt out because the sheet is the artefact a wage claim is argued from: a
  // day with an arrival and no departure has not been verified, and it must not
  // be transcribed as a present day just because somebody was seen at the gate.
  lines.push(row(["A missing punch is not counted as present. It must be regularised first."]));
  lines.push(row([]));

  const dayCols = Array.from({ length: totalDays }, (_, i) => String(i + 1));
  lines.push(row(["Employee", "ID", ...dayCols, "Present", "Working days", "A", "L", "Signature"]));

  musterRows.forEach((r) => {
    // the same tally the grid and the print sheet use
    const t = tallyRow(r.days);
    lines.push(
      row([
        r.name,
        r.aliasId || "",
        ...r.days.map(printSymbol),
        t.present,
        t.workingDays,
        t.absent,
        t.leave,
        "", // signed on paper
      ])
    );
  });

  lines.push(row([]));
  // Named, because an unsigned assertion with no author is not evidence of
  // anything. The countersignature is what makes the sheet a two-party record.
  lines.push(row(["Corrected days are marked *. Signing confirms these hours are accurate."]));
  lines.push(row([]));
  lines.push(row(["Supervisor", "", "Signature", "", "Date"]));

  return lines.join("\n");
};

export const downloadCsv = (filename, contents) => {
  // the BOM keeps Excel from mangling non-ASCII names on open
  const blob = new Blob([`﻿${contents}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
